import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import {
  ResumePDFLink,
  ResumePDFText,
} from "components/Resume/ResumePDF/common";
import { capitalize } from "./capitalize";
import { store } from "lib/redux/store";
import { handleClick } from "lib/utils/navigator";
export const TemplateThirteenPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
  
}: {
  themeColor: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);


  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return (
    <div
    onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        gap: spacing["4"],
        marginTop: spacing["10"],
        cursor:"pointer"
      }}
    >
      {heading && (
        <div
          style={{
            ...styles.flexCol,
            padding: spacing["1.5"],
            width: "100%",
            textAlign: "center",
            backgroundColor:`rgba(${r}, ${g}, ${b},.07)`,
            cursor: "pointer"
          }}
        >
            <Text
            
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt",
              color: themeColor || DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {capitalize(heading)}
          </Text>
        </div>
      )}
      <View
        style={{
          ...styles.flexCol,
          gap: spacing[2],
        
        }}
      >
        {children}
      </View>
    </div>
  );
};


export const ResumePDFDescriptionWithWhiteFont = ({
  items,
}: {
  items: string[];
}) => {
 
  if (!items || items.length === 0) {
    return null; 
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText
              key={Math.random()}
              underline={true}
              themeColor="white"
            >
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText themeColor="white">
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4,
                  paddingRight: 4, 
                  lineHeight: 1.3,
                }}
                bold={true}
                themeColor="white"
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                themeColor="white"
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (node: Node, themeColor: string = "black"): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        return (
          <View>
            <ResumePDFText
              style={{ lineHeight: 1.3, flexGrow: 1 }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                    themeColor={themeColor}
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        return (
          <View style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};


export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

export const ResumePDFDescription = ({ items, themeColor }: { items: string[], themeColor?: string }) => {
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{renderNode(node,themeColor)}</React.Fragment>
      ))}
    </View>
  );
};

//component for left column 
export const TemplateThirteenLeftColumnSectionWithSubsections = ({
  themeColor,
  heading,
  children,
}: {
  themeColor: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  
  if (childrenArray.length === 0) {
    return null;
  }

  // First child  grouped with heading 
  const [firstChild, ...restChildren] = childrenArray;
  
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      {/* Heading + first subsection  */}
      <View wrap={false} style={{ ...styles.flexCol }}>
        {heading && (
          <div
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"],
              width: "100%",
              textAlign: "center",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
              marginTop: spacing["10"],
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </div>
        )}
        {firstChild && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["2"],
              marginTop: spacing["4"],
            }}
          >
            {firstChild}
          </View>
        )}
      </View>
      {/* Remaining subsections */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["2"],
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

//  section component for right column 
export const TemplateThirteenRightColumnSectionWithSubsections = ({
  themeColor,
  heading,
  children,
}: {
  themeColor: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  
  if (childrenArray.length === 0) {
    return null;
  }

  // First child  grouped with heading
  const [firstChild, ...restChildren] = childrenArray;
  
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      {/* Heading + first subsection */}
      <View wrap={false} style={{ ...styles.flexCol }}>
        {heading && (
          <div
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"],
              width: "100%",
              textAlign: "center",
              // color: "white",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
              marginTop: spacing["10"],
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </div>
        )}
        {firstChild && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["2"],
              marginTop: spacing["4"],
            }}
          >
            {firstChild}
          </View>
        )}
      </View>
      {/* Remaining subsections */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["2"],
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

// single-entry section for left column
export const TemplateThirteenLeftColumnSection = ({
  themeColor,
  heading,
  children,
}: {
  themeColor: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      <View
        wrap={false} // Prevent orphan sections
        style={{
          ...styles.flexCol,
          gap: spacing["4"],
          marginTop: spacing["10"],
        }}
      >
        {heading && (
          <div
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"],
              width: "100%",
              textAlign: "center",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </div>
        )}
        {children && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["2"],
            }}
          >
            {children}
          </View>
        )}
      </View>
    </div>
  );
};

//new single-entry section for right column
export const TemplateThirteenRightColumnSection = ({
  themeColor,
  heading,
  children,
}: {
  themeColor: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      <View
        wrap={false} // Prevent orphan sections
        style={{
          ...styles.flexCol,
          gap: spacing["4"],
          marginTop: spacing["10"],
        }}
      >
        {heading && (
          <div
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"],
              width: "100%",
              // color: "white",
              textAlign: "center",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,// "white" 
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white" ,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </div>
        )}
        {children && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["2"],
            }}
          >
            {children}
          </View>
        )}
      </View>
    </div>
  );
};
